/**
 * Лента работ: страница берётся в форме контракта, обход идёт по курсору.
 *
 * Обход **заканчивается**: курсор следующей страницы существует только вместе
 * с признаком «есть ещё». Непустой курсор на последней странице — приманка, на
 * которой обход зацикливался, и ловил её ручной прогон, а не тесты.
 *
 * Порядок и состав страницы — от сервера. Ни сортировки, ни отбора: правила
 * видимости нет ни на сервере, ни где-либо ещё, и фильтр, появившийся здесь,
 * стал бы правилом, которого нет ни у кого. Статус работы показывается тем
 * значением, которое пришло: таблица «статус → подпись» это перечисление в
 * клиенте.
 *
 * Отказ посреди обхода не стирает полученного: страницы уже пришли и уже
 * верны, а курсор остаётся — повторная дозагрузка продолжает с того же места.
 *
 * Отвергнутый токен — не отказ, а другой исход: экран отказа вместо экрана
 * входа выглядит поломкой сервера. Различаются они машинным кодом.
 */

import { ApiFailure, UNAUTHENTICATED_CODE } from '../api/client';
import type { ApiClient } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { Work } from '../api/contract';
import type { Strings } from '../i18n';

export interface WorksScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly onUnauthenticated: () => void;
}

export interface WorksScreen {
  readonly element: HTMLElement;
  readonly works: readonly Work[];
  readonly hasMore: boolean;
  readonly failed: boolean;
  /** Обход с начала: первая страница запрашивается без курсора. */
  load(): Promise<void>;
  /** Следующая страница по курсору предыдущей. Без курсора — ничего. */
  loadMore(): Promise<void>;
}

export function createWorksScreen(options: WorksScreenOptions): WorksScreen {
  const { client, strings, onUnauthenticated } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const list = document.createElement('ul');
  const notice = document.createElement('p');
  const more = document.createElement('button');

  heading.textContent = strings.works_title;
  notice.textContent = strings.works_failed;
  more.textContent = strings.works_more;
  more.type = 'button';

  element.append(heading, list);

  let works: readonly Work[] = [];
  let cursor: string | null = null;
  let hasMore = false;
  let failed = false;

  function row(item: Work): HTMLElement {
    const node = document.createElement('li');

    node.textContent = `${item.id} ${item.market} ${item.status} ${item.created_at}`;

    return node;
  }

  function render(): void {
    list.replaceChildren(...works.map(row));

    if (failed) {
      element.append(notice);
    } else {
      notice.remove();
    }

    if (hasMore) {
      element.append(more);
    } else {
      more.remove();
    }
  }

  async function loadPage(from: string | null): Promise<void> {
    try {
      const page = from === null ? await client.listWorks() : await client.listWorks({ cursor: from });

      works = [...works, ...page.data];
      cursor = nextPageCursor(page.meta);
      hasMore = cursor !== null;
      failed = false;
    } catch (error) {
      if (error instanceof ApiFailure && error.code === UNAUTHENTICATED_CODE) {
        // Возврат на вход и состояние отказа взаимоисключающи всегда, а не
        // только из чистого состояния: вкладка уходит на вход, и отказ ленты
        // после этого не относится ни к чему. Полученные страницы при этом
        // остаются — они уже пришли и уже верны.
        failed = false;
        onUnauthenticated();
      } else {
        // Полученное остаётся на экране, курсор не теряется: сбой следующей
        // страницы не делает предыдущие неверными.
        failed = true;
      }
    }

    render();
  }

  async function load(): Promise<void> {
    works = [];
    cursor = null;
    hasMore = false;
    failed = false;

    await loadPage(null);
  }

  async function loadMore(): Promise<void> {
    if (!hasMore || cursor === null) {
      return;
    }

    await loadPage(cursor);
  }

  more.addEventListener('click', () => {
    void loadMore();
  });

  render();

  return {
    element,

    get works(): readonly Work[] {
      return works;
    },

    get hasMore(): boolean {
      return hasMore;
    },

    get failed(): boolean {
      return failed;
    },

    load,
    loadMore,
  };
}
